import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, GripVertical } from "lucide-react";
import {
  listCityInfo,
  createCityInfo,
  updateCityInfo,
  deleteCityInfo,
  type CityInfo,
  type CityInfoWrite,
  type QuickFact,
  type CitySection,
} from "@/lib/city-api";

type Draft = {
  id?: string;
  name: string;
  subtitle: string;
  quick_facts: QuickFact[];
  sections: CitySection[];
  is_active: boolean;
};

const EMPTY: Draft = {
  name: "",
  subtitle: "",
  quick_facts: [{ label: "", value: "" }],
  sections: [{ title: "", content: "" }],
  is_active: true,
};

export const Route = createFileRoute("/admin/city")({ component: CityAdmin });

function CityAdmin() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Draft | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-city-info"],
    queryFn: listCityInfo,
  });

  // Blank rows are scaffolding for the editor UI, not content — drop them before saving.
  const toWrite = (d: Draft): CityInfoWrite => ({
    name: d.name.trim(),
    subtitle: d.subtitle.trim(),
    quick_facts: d.quick_facts.filter((f) => f.label.trim() || f.value.trim()),
    sections: d.sections.filter((s) => s.title.trim() || s.content.trim()),
    is_active: d.is_active,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-city-info"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: Draft) => createCityInfo(toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("City info created");
      setEdit(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: Draft) => updateCityInfo(d.id as string, toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("City info updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteCityInfo(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = () => {
    if (!edit) {
      return;
    }
    if (!edit.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (edit.id) {
      updateMut.mutate(edit);
    } else {
      createMut.mutate(edit);
    }
  };

  const remove = (id: string) => {
    if (confirm("Delete this city info?")) {
      deleteMut.mutate(id);
    }
  };

  const openEdit = (c: CityInfo) =>
    setEdit({
      id: c.id,
      name: c.name,
      subtitle: c.subtitle,
      quick_facts: c.quick_facts.length ? c.quick_facts : [{ label: "", value: "" }],
      sections: c.sections.length ? c.sections : [{ title: "", content: "" }],
      is_active: c.is_active,
    });

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold">Ahmedabad Details</h2>
          <p className="text-sm text-muted-foreground">
            The city guide shown in the Discover Ahmedabad section of the homepage
          </p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary py-2 text-sm">
          <Plus className="h-4 w-4" /> Add City Info
        </button>
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      )}
      {isError && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load city info"}
        </div>
      )}
      {!isLoading && !isError && list.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No city info yet
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {list.map((c) => (
          <div key={c.id} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-serif text-lg font-bold">{c.name}</h3>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                      c.is_active
                        ? "border-primary/30 bg-primary/15 text-primary"
                        : "border-border bg-muted text-muted-foreground"
                    }`}
                  >
                    {c.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{c.subtitle}</p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => openEdit(c)}
                  title="Edit"
                  className="rounded-lg p-1.5 hover:bg-muted"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => remove(c.id)}
                  title="Delete"
                  className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {c.quick_facts.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {c.quick_facts.map((f, i) => (
                  <div key={i} className="rounded-xl bg-muted/50 p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {f.label}
                    </div>
                    <div className="mt-0.5 text-xs font-medium">{f.value}</div>
                  </div>
                ))}
              </div>
            )}

            {c.sections.length > 0 && (
              <div className="mt-3 text-xs text-muted-foreground">
                {c.sections.length} section{c.sections.length === 1 ? "" : "s"} ·{" "}
                {c.sections.map((s) => s.title).join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>

      {edit && (
        <Editor
          draft={edit}
          setDraft={setEdit}
          onSave={save}
          onClose={() => setEdit(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function Editor({
  draft,
  setDraft,
  onSave,
  onClose,
  saving,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const setFact = (i: number, patch: Partial<QuickFact>) =>
    setDraft({
      ...draft,
      quick_facts: draft.quick_facts.map((f, j) => (j === i ? { ...f, ...patch } : f)),
    });
  const setSection = (i: number, patch: Partial<CitySection>) =>
    setDraft({
      ...draft,
      sections: draft.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)),
    });

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-3xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold">{draft.id ? "Edit" : "Add"} City Info</h3>
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Name" required>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Ahmedabad"
              className="inp"
            />
          </Field>
          <Field label="Subtitle">
            <input
              value={draft.subtitle}
              onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
              placeholder="Gujarat, India"
              className="inp"
            />
          </Field>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
          />
          Active (shown on the homepage)
        </label>

        {/* Quick facts */}
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Quick Facts
            </h4>
            <button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  quick_facts: [...draft.quick_facts, { label: "", value: "" }],
                })
              }
              className="btn-outline py-1 text-xs"
            >
              <Plus className="h-3 w-3" /> Add fact
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {draft.quick_facts.map((f, i) => (
              // Widths live on the wrappers: `.inp` forces width:100% and would otherwise override
              // any width utility placed directly on the input.
              <div key={i} className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="w-1/3 shrink-0">
                  <input
                    value={f.label}
                    onChange={(e) => setFact(i, { label: e.target.value.toUpperCase() })}
                    placeholder="FOUNDED"
                    className="inp font-mono text-xs"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <input
                    value={f.value}
                    onChange={(e) => setFact(i, { value: e.target.value })}
                    placeholder="1411 CE by Sultan Ahmad Shah"
                    className="inp"
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      quick_facts: draft.quick_facts.filter((_, j) => j !== i),
                    })
                  }
                  title="Remove"
                  className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Sections
            </h4>
            <button
              type="button"
              onClick={() =>
                setDraft({ ...draft, sections: [...draft.sections, { title: "", content: "" }] })
              }
              className="btn-outline py-1 text-xs"
            >
              <Plus className="h-3 w-3" /> Add section
            </button>
          </div>
          <div className="mt-2 space-y-3">
            {draft.sections.map((s, i) => (
              <div key={i} className="rounded-xl border border-border p-3">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <input
                      value={s.title}
                      onChange={(e) => setSection(i, { title: e.target.value })}
                      placeholder="A City of Faith & Craft"
                      className="inp font-semibold"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({ ...draft, sections: draft.sections.filter((_, j) => j !== i) })
                    }
                    title="Remove"
                    className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <textarea
                  rows={4}
                  value={s.content}
                  onChange={(e) => setSection(i, { content: e.target.value })}
                  placeholder="Section body…"
                  className="inp mt-2"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="btn-outline py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
